import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';

import {trackEvent} from 'analytics/Analytics';
import AntibodyChainLegend from 'antibodies/antibody-map/AntibodyChainLegend';
import AntibodyDragWrapper from 'antibodies/antibody-map/AntibodyDragWrapper';
import {
  clampAntibodyPreviewCoordinatesToBoundingBox,
  getDefaultAntibodyPreviewCoordinates,
} from 'antibodies/antibody-map/AntibodyMapUtils';
import ComponentPreview from 'antibodies/antibody-map/ComponentPreview';
import useAntibodyMapState, {
  DEFAULT_ANTIBODY_DRAG_COORDINATE,
} from 'antibodies/antibody-map/useAntibodyMapState';
import {Antibody as GQLAntibody, AntibodyWithVersion} from 'antibodies/AntibodyTypedefs';
import useAntibodyUIState from 'antibodies/hooks/useAntibodyUIState';
import {useAntibodyVersionQuery} from 'antibodies/hooks/useAntibodyVersionQuery';
import Antibody from 'antibodies/visualization/Antibody';
import {assert} from 'Asserts';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

function assertIsNonNullishAntibodyAndVersion(
  antibody: GQLAntibody | null
): asserts antibody is AntibodyWithVersion {
  assert(antibody != null && antibody.version != null, 'Unexpected nullish antibody or version');
}

export interface AntibodyMapProps {
  antibodyId: string;
}

export default function AntibodyMap({antibodyId}: AntibodyMapProps): JSX.Element {
  const {
    state: {openVersionId},
  } = useAntibodyUIState(antibodyId);
  useOnMount(() => {
    trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.OPENED_ANTIBODY_ENTITY, {antibodyId});
  });
  return <AntibodyMapContent antibodyId={antibodyId} versionId={openVersionId} mapStateKey={antibodyId} />;
}

export interface AntibodyMapContentProps {
  antibodyId: string;
  /** Version of the antibody to render. If null, the map shows a load error. */
  versionId: string | null;
  /** Identifies this map's interactivity state; see `useAntibodyMapState`. */
  mapStateKey: string;
}

/**
 * The antibody glyph, chain legend, and component preview for a version of an antibody. Renders the given
 * version and keeps its interactivity state under `mapStateKey`, so entities that embed an antibody can
 * render its map without affecting the antibody's own tabs.
 */
export function AntibodyMapContent({
  antibodyId,
  versionId,
  mapStateKey,
}: AntibodyMapContentProps): JSX.Element {
  const {status, antibody} = useAntibodyVersionQuery(antibodyId, versionId);

  const {
    state: {
      dragState: {x, y},
      expandPreview,
      hoveredRef,
      selectedRef,
      showLabels,
      showMinimap,
    },
    setExpandPreview,
    setHoveredRef,
    setSelectedRef,
  } = useAntibodyMapState(mapStateKey);

  const domainPreviewRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleExpandPreview = (): void => {
    let newX = x;
    let newY = y;
    if (wrapperRef.current != null) {
      // If we are expanding the preview for the first time (i.e., if we are not currently expanded and the
      // location of the antibody viz is at default)...
      if (
        !expandPreview &&
        x === DEFAULT_ANTIBODY_DRAG_COORDINATE &&
        y === DEFAULT_ANTIBODY_DRAG_COORDINATE
      ) {
        // ...put the antibody preview in the default location.
        [newX, newY] = getDefaultAntibodyPreviewCoordinates(wrapperRef.current);
      }
      // Clamp the coordinates just in case we've changed viewbox size since last viewing the expanded preview.
      [newX, newY] = clampAntibodyPreviewCoordinatesToBoundingBox(wrapperRef.current, newX, newY);
    }
    setExpandPreview(!expandPreview, newX, newY);
  };

  if (status === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (status === QueryResultType.ERROR || status === QueryResultType.SKIPPED) {
    return <span>Error loading protein</span>;
  }

  assertIsNonNullishAntibodyAndVersion(antibody);

  const format = antibody.format;
  const proteinIdsByRef: Partial<Record<number, string>> = {};
  const inaccessibleProteinIdsByRef: Record<number, string> = {};
  const componentLabelByRef: Record<number, string> = {};
  // Set labels based on format domains because `antibody.version.domains` will be missing optional domains
  // with no associated sequence.
  antibody.format.domains.forEach((domain) => {
    for (const ref of domain.refs) {
      componentLabelByRef[ref] = domain.label;
    }
  });
  antibody.version.domains.forEach((domain) => {
    const {entity} = domain;
    for (const ref of domain.refs) {
      if (entity == null) {
        continue;
      }
      proteinIdsByRef[ref] = isInaccessible(entity) ? entity.invalidId : entity.id;
      if (isInaccessible(entity)) {
        inaccessibleProteinIdsByRef[ref] = entity.invalidId;
      }
    }
  });
  antibody.version.chains.forEach((chain) => {
    const {entity} = chain;
    for (const ref of chain.refs) {
      componentLabelByRef[ref] = chain.label;
      proteinIdsByRef[ref] = isInaccessible(entity) ? entity.invalidId : entity.id;
      if (isInaccessible(entity)) {
        inaccessibleProteinIdsByRef[ref] = entity.invalidId;
      }
    }
  });

  const interactivity = {
    hoveredRef,
    selectedRef,
    setHoveredRef,
    setSelectedRef,
    proteinIdsByRef,
    inaccessibleProteinIdsByRef,
  };

  return (
    <div className="react-tab-container">
      <div
        className={css(styles.wrapper)}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        ref={wrapperRef}
        {...getTestProps({component: 'AntibodyMap', element: 'root'})}
      >
        {antibody.version.outOfSync && (
          <div className={css(styles.outOfSyncBanner)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text={`Antibody is out of sync with its component entities. ${antibody.version.outOfSyncReason}`}
            />
          </div>
        )}
        <AntibodyChainLegend
          antibodyFormat={format}
          antibodyVersion={antibody.version}
          interactivity={interactivity}
          antibodyId={antibodyId}
        />
        {(!expandPreview || showMinimap) && (
          <AntibodyDragWrapper mapStateKey={mapStateKey} domainPreviewRef={domainPreviewRef}>
            <Antibody
              format={format}
              interactivity={interactivity}
              showLabels={showLabels}
              showPopovers={true}
              antibodyId={antibodyId}
            />
          </AntibodyDragWrapper>
        )}
        {selectedRef != null && (
          <div
            className={css(expandPreview ? styles.previewWrapperExpanded : styles.previewWrapper)}
            ref={domainPreviewRef}
          >
            <ComponentPreview
              componentLabel={componentLabelByRef[selectedRef]}
              antibodyId={antibodyId}
              mapStateKey={mapStateKey}
              selectedProteinId={proteinIdsByRef[selectedRef] ?? null}
              toggleExpandPreview={toggleExpandPreview}
              closePreview={() => {
                setSelectedRef(null);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    userSelect: 'none',
  },
  editButton: {
    width: 'fit-content',
    marginLeft: 'auto',
    marginTop: 8,
    marginBottom: 32,
  },
  previewWrapper: {
    flex: '0 0 280px',
    minHeight: 0,
    height: '280px',
  },
  previewWrapperExpanded: {
    flex: '1 0 auto',
    minHeight: 0,
  },
  outOfSyncBanner: {
    marginBottom: 10,
  },
});
